import type { StatusTone } from '../contracts/common';
import type {
  GoogleOAuthOutcome,
  IntegrationAuthMethod,
  IntegrationGroup,
  IntegrationKind,
  IntegrationStatus,
  MailboxScope,
} from '../contracts/integrations';

/** Status integracji (Figma 12 Ustawienia → Integracje; kolor tylko, gdy niesie znaczenie). */
export const INTEGRATION_STATUS_LABELS: Record<IntegrationStatus, string> = {
  disconnected: 'Niepodłączona',
  connected: 'Działa',
  error: 'Błąd',
  auth_lost: 'Utracono autoryzację',
  simulated: 'Symulacja',
};

export const INTEGRATION_STATUS_TONES: Record<IntegrationStatus, StatusTone> = {
  disconnected: 'neutral',
  connected: 'success',
  error: 'danger',
  auth_lost: 'warning',
  simulated: 'accent',
};

export const INTEGRATION_KIND_LABELS: Record<IntegrationKind, string> = {
  // rodzaj `gmail` = skrzynka pocztowa: Gmail (Google Workspace) albo IMAP/SMTP (np. Zenbox)
  gmail: 'Skrzynka pocztowa',
  // kalendarz zwykłego konta Google: tajny adres iCal (DECISIONS 2026-10-07 „Bez Google Workspace”) albo OAuth
  google_calendar: 'Kalendarz',
  invoicing: 'System fakturowania',
  ai: 'Dostawca AI',
  smtp: 'Poczta systemowa (SMTP)',
  storage: 'Magazyn plików',
  monitoring: 'Monitoring stron',
};

/** Podpis pod nazwą (Figma 12: „Wystawianie faktur z zestawień czasu”). */
export const INTEGRATION_KIND_DESCRIPTIONS: Record<IntegrationKind, string> = {
  gmail: 'Maile klientów w profilu i Inboxie, wysyłka po akceptacji',
  google_calendar: 'Spotkania z Twojego kalendarza i najbliższe terminy',
  invoicing: 'Wystawianie faktur z zestawień czasu',
  ai: 'Podsumowania, szkice odpowiedzi, zadania i czas z tekstu',
  smtp: 'Maile logowania i powiadomień',
  storage: 'Załączniki i pliki',
  monitoring: 'Dostępność stron klientów',
};

export const INTEGRATION_PROVIDER_LABELS: Record<string, string> = {
  fake: 'Symulacja',
  gmail: 'Gmail',
  imap: 'IMAP/SMTP (np. Zenbox)',
  google: 'Google',
  ics: 'Link iCal',
  fakturownia: 'Fakturownia',
  anthropic: 'Anthropic (Claude)',
  openai: 'OpenAI',
  smtp: 'SMTP',
  local: 'Dysk serwera',
  s3: 'S3',
  http: 'HTTP',
};

export function integrationProviderLabel(provider: string): string {
  return INTEGRATION_PROVIDER_LABELS[provider] ?? provider;
}

/** Grupa na liście integracji: firmowa (zarządza admin) albo osobista („Twoje”). */
export function integrationScopeLabel(ownerUserId: string | null): string {
  return ownerUserId ? 'Twoje' : 'Firma';
}

/** Nagłówki grup listy (Figma 12). */
export const INTEGRATION_GROUP_LABELS: Record<IntegrationGroup, string> = {
  company: 'Firma · zarządza admin',
  mine: 'Twoje',
  member: 'Konta osób z zespołu',
};

export const INTEGRATION_AUTH_METHOD_LABELS: Record<IntegrationAuthMethod, string> = {
  none: 'Bez logowania',
  api_key: 'Klucz API',
  oauth_google: 'Logowanie Google',
  server_env: 'Konfiguracja serwera',
  password: 'Login i hasło',
  secret_url: 'Tajny adres iCal',
};

/** Rodzaj skrzynki pocztowej (DECISIONS 6.10 i 7.10: kontakt@ firmowa, skrzynki osób prywatne). */
export const MAILBOX_SCOPE_LABELS: Record<MailboxScope, string> = {
  company: 'Firmowa',
  personal: 'Osobista',
};

export const MAILBOX_SCOPE_DESCRIPTIONS: Record<MailboxScope, string> = {
  company: 'Wątki widzą osoby z dostępem do klienta.',
  personal: 'Wątki widzisz tylko Ty (możesz udostępnić wybrany wątek zespołowi).',
};

/**
 * Podpowiedzi formularza skrzynki IMAP/SMTP. Adresów serwerów nie zgadujemy: każdy hosting podaje je w panelu przy
 * koncie pocztowym (dane do konfiguracji programu pocztowego).
 */
export const IMAP_FORM_HINTS = {
  intro:
    'Dane serwerów znajdziesz w panelu hostingu (np. Zenbox) przy koncie pocztowym — w danych do konfiguracji programu pocztowego. Połączenie jest zawsze szyfrowane.',
  imapHost: 'Serwer poczty przychodzącej (IMAP) z panelu hostingu',
  imapPort: '993 — SSL/TLS',
  smtpHost: 'Serwer poczty wychodzącej (SMTP) z panelu hostingu',
  smtpPort: '465 — SSL/TLS albo 587 — STARTTLS',
  login: 'Zostaw puste, jeśli login to adres skrzynki',
  password: 'Zapisujemy je zaszyfrowane i nigdy nie pokazujemy.',
  passwordKeep: 'Zostaw puste, aby zachować zapisane hasło. Zapisujemy je zaszyfrowane i nigdy nie pokazujemy.',
  /** zmiana serwera, portu albo loginu: zapisane hasło nie idzie na nowy serwer (przegląd Fali 8) */
  passwordServerChanged:
    'Zmieniasz serwer, port albo login — wpisz hasło skrzynki ponownie (zapisane hasło nie trafi na nowy serwer).',
} as const;

/** Baner nad listą, gdy żadna integracja zewnętrzna nie działa naprawdę. */
export const INTEGRATIONS_SIMULATION_BANNER = 'System działa w trybie symulacji — nic nie wychodzi na zewnątrz';

/** Notka pod statusem „Symulacja”. */
export const INTEGRATION_SIMULATED_HINT = 'Dane przykładowe, nic nie wychodzi na zewnątrz';

/** Komunikat po powrocie z Google (parametr `google` w adresie /ustawienia/integracje). */
export const GOOGLE_OAUTH_OUTCOME_MESSAGES: Record<GoogleOAuthOutcome, string> = {
  connected: 'Połączono konto Google',
  denied: 'Nie udzielono zgody w Google. Nic nie zostało zmienione',
  state: 'Połączenie przerwane albo otwarte w innej sesji. Zacznij od nowa',
  expired: 'Połączenie trwało za długo. Zacznij od nowa',
  exchange: 'Google odrzucił kod logowania. Spróbuj ponownie',
  no_refresh_token: 'Google nie zwrócił stałego dostępu. Usuń dostęp aplikacji w koncie Google i połącz ponownie',
  account_mismatch: 'Zalogowano na inne konto Google niż ta skrzynka',
  session: 'Sesja wygasła. Zaloguj się i połącz ponownie',
  unavailable: 'Logowanie Google podłączymy w fazie konektorów',
  step_up: 'Potwierdź hasło i spróbuj ponownie',
  forbidden: 'Brak uprawnień do tej integracji',
  error: 'Nie udało się połączyć konta Google',
};
